import { FaceSmileIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PaperClipIcon } from "@heroicons/react/20/solid";
import EmojiPicker from "emoji-picker-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDebounce } from "../hooks/useDebounce";
import ErrorModal from "./ErrorModal";
import PrimaryButton from "./PrimaryButton";

const ThreadSendMessage = ({
  chatbotUuid,
  selectedThread,
  api,
  fetchThread,
}) => {
  const [errorModalMsg, setErrorModalMsg] = useState(null);
  const [isCurrentlyTyping, setIsCurrentlyTyping] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const [filePreview, setFilePreview] = useState(null);
  const [fileName, setFileName] = useState(null);

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleInputChange = e => {
    setNewMessage(e.target.value);

    if (!isCurrentlyTyping) {
      setIsCurrentlyTyping(true);
      sendTypingNotification(true);
    }

    debouncedStopTyping();
  };

  const handleUploadAttachment = async () => {
    try {
      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

      if (filePreview.size > MAX_FILE_SIZE) {
        setErrorModalMsg(
          "File size exceeds 10MB. Please upload a smaller file."
        );
        alert("File size exceeds 10MB. Please upload a smaller file.");
        return;
      }

      setIsTyping(true);

      const formData = new FormData();
      formData.append("file", filePreview);

      const config = {
        onUploadProgress: progressEvent => {
          const percentage = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentage);
        },
      };

      const attachEndpoint = `/api/chatbot/${chatbotUuid}/conversation/${selectedThread.conversation_uuid}/attach`;

      await api.post(attachEndpoint, formData, config);

      fetchThread({ thread: selectedThread });

      setNewMessage("");
      setFilePreview(null);
      setFileName(null);
      setUploadProgress(0);
    } catch (err) {
      setErrorModalMsg("Error attaching file, please contact us for help.");
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      setIsTyping(true);

      if (isCurrentlyTyping) {
        setIsCurrentlyTyping(false);
        if (!debouncedStopTyping.current) {
          sendTypingNotification(false);
        }
      }

      const payload = filePreview
        ? {
            file: filePreview,
            fileName,
            company_id: Number(sessionStorage.getItem("company_id")),
            chatbot_uuid: chatbotUuid,
            conversation_uuid: selectedThread.conversation_uuid,
          }
        : {
            text: newMessage,
            company_id: Number(sessionStorage.getItem("company_id")),
            chatbot_uuid: chatbotUuid,
            conversation_uuid: selectedThread.conversation_uuid,
          };

      await api.post("/api/save-chat-response", payload);

      fetchThread({ thread: selectedThread });

      setNewMessage("");
      setFilePreview(null);
      setFileName(null);
    } catch (err) {
      setErrorModalMsg("Error sending message, please contact us for help.");

      console.error("Failed to send message", err);
    } finally {
      setIsTyping(false);
    }
  };

  const handleEmojiSelect = emojiObject => {
    setNewMessage(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  const handleFileUpload = e => {
    const file = e.target.files[0];
    if (file) {
      setFilePreview(file);
      setFileName(file.name);
      setNewMessage("");
    }
  };

  const removeFilePreview = () => {
    setFilePreview(null);
    setFileName(null);
  };

  const sendTypingNotification = useCallback(
    async isTyping => {
      try {
        await api.post("/api/typing-indicator", {
          conversation_uuid: selectedThread.conversation_uuid,
          is_typing: isTyping,
        });
      } catch (error) {
        console.error("Error notifying server of typing indication.");
      }
    },
    [selectedThread.conversation_uuid, api]
  );

  const onKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const debouncedStopTyping = useDebounce(() => {
    setIsCurrentlyTyping(false);
    sendTypingNotification(false);
  }, 1000);

  useEffect(() => {
    return () => {
      if (isCurrentlyTyping && !debouncedStopTyping.current) {
        sendTypingNotification(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentlyTyping, sendTypingNotification]);

  return (
    <>
      <div className="relative">
        {filePreview ? (
          <div className="relative w-full">
            <textarea
              className="w-full p-2 border border-gray-300 text-gray-400 rounded-lg pr-10 resize-none relative"
              rows={4}
              value={fileName}
              disabled
            />

            {filePreview && uploadProgress > 0 && (
              <div className="relative w-10/12">
                <div
                  className="absolute bottom-2 left-2 h-1 bg-blue-500"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>
        ) : (
          <textarea
            className="w-full p-2 border-gray-300 border rounded-lg pr-10 resize-none focus:border-primary placeholder-gray-500"
            rows={4}
            placeholder="Send a message"
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
          />
        )}

        <div className="actions absolute bottom-2 right-2 flex items-center gap-2">
          {filePreview ? (
            <>
              <button className="text-red-600" onClick={removeFilePreview}>
                <XMarkIcon className="size-6" />
              </button>

              <button
                className={`p-4 py-3 text-sm font-bold bg-primary shadow hover:bg-primary text-white rounded-lg transition-all duration-200 ease-in-out
                  ${isTyping || (!newMessage && !filePreview) ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={handleUploadAttachment}
                disabled={isTyping || (!newMessage && !filePreview)}
              >
                Attach
              </button>
            </>
          ) : (
            <>
              <button
                className="text-gray-500 relative"
                onClick={() => setShowEmojiPicker(prev => !prev)}
              >
                {showEmojiPicker && (
                  <div className="z-10 absolute bottom-6 right-6">
                    <EmojiPicker onEmojiClick={handleEmojiSelect} />
                  </div>
                )}
                <FaceSmileIcon className="size-6" />
              </button>

              <button>
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer text-gray-500"
                >
                  <PaperClipIcon className="size-6 mb-0.5" />
                </label>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </button>

              <div>
                <PrimaryButton
                  onClick={handleSendMessage}
                  disabled={isTyping || (!newMessage && !filePreview)}
                  type="button"
                  loading={isTyping}
                >
                  Send
                </PrimaryButton>
              </div>
            </>
          )}
        </div>
      </div>

      <ErrorModal
        isOpen={errorModalMsg}
        onClose={() => {
          setErrorModalMsg(null);
          setUploadProgress(0);
        }}
        errorMessage={errorModalMsg}
      />
    </>
  );
};

export default ThreadSendMessage;
